/**
 * The KI concierge inside the Wo field (spec §4.1, §4.3 step 4).
 *
 * A whole sentence typed where a place belongs ("Meetingraum für 6 in Zürich") goes to the same
 * endpoint and through the same intent mapping as the old SearchAiConcierge bar
 * (util/aiConcierge.js). The result is then split into the two answers the unified search owns —
 * WHERE and SPACE TYPE — and everything else (dates, seats, booking type, price, keywords, sort,
 * a category outside the three groups such as lounges) as extra params, which keep the target
 * on /s (buildSearchTarget).
 *
 * Never dead-ends: a 503 marks the concierge unavailable for the session (the Wo field then offers
 * a keyword search instead) and any failure falls back to the redacted text as keywords.
 */
import { aiConciergeParse } from '../../util/api';
import { track } from '../../util/analytics';
import {
  conciergeRequestPayload,
  intentToSearchParams,
  markAiConciergeUnavailable,
  mergeLocationIntoKeywords,
  normalizeSearchParams,
  redactPii,
  sliceWholeChars,
} from '../../util/aiConcierge';
import { encodeLatLngBounds } from '../../util/urlHelpers';
import {
  CATEGORY_PARAM,
  WHERE_PLACE,
  citySlugForPlaceName,
  spaceTypeForCategoryParam,
} from '../../util/unifiedSearch';

import GeocoderMapbox from '../LocationAutocompleteInput/GeocoderMapbox';

// Matches MAX_TEXT_LENGTH in server/api/ai-concierge.js.
export const MAX_TEXT_LENGTH = 300;

// Free-text hygiene shared with SearchAiConcierge (util/aiConcierge.js); re-exported for the tests.
export { redactPii, sliceWholeChars };

/** The free text as a plain keyword search (explicit choice or fallback). */
export const keywordSearch = text => ({
  where: null,
  spaceType: undefined,
  extraParams: { keywords: redactPii(sliceWholeChars(`${text || ''}`.trim(), MAX_TEXT_LENGTH)) },
  fallback: false,
});

/** Forward-geocode a place the intent named but our cities do not cover. Never throws. */
const geocodePlace = async (locationName, locale) => {
  try {
    const geocoder = new GeocoderMapbox();
    const { predictions } = await geocoder.getPlacePredictions(locationName, null, locale);
    if (!predictions || predictions.length === 0) {
      return null;
    }
    const place = await geocoder.getPlaceDetails(predictions[0]);
    return place?.bounds
      ? { address: place.address || locationName, boundsStr: encodeLatLngBounds(place.bounds) }
      : null;
  } catch (e) {
    return null;
  }
};

const PASSED_THROUGH = ['dates', 'seats', 'pub_listingType', 'price', 'keywords', 'sort'];

/**
 * Split concierge search params into WHERE, SPACE TYPE and the rest.
 *
 * @param {Object} searchParams normalised params from intentToSearchParams
 * @param {Object} p
 * @param {Array} p.groups resolved space-type groups
 * @param {string} [p.citySlug] the intent's city slug (enum-constrained by the server schema)
 * @returns {{ where: Object|null, spaceType: string|null|undefined, extraParams: Object }}
 *   `spaceType` undefined = the sentence did not speak to it (the draft's choice stays)
 */
export const splitConciergeParams = (searchParams = {}, { groups = [], citySlug } = {}) => {
  const { address, bounds } = searchParams;
  const boundsStr = typeof bounds === 'string' ? bounds : bounds ? encodeLatLngBounds(bounds) : '';
  const where =
    address && boundsStr
      ? {
          kind: WHERE_PLACE,
          address,
          boundsStr,
          label: `${address}`.split(',')[0],
          citySlug: citySlug || citySlugForPlaceName(address),
        }
      : null;

  const extraParams = {};
  let spaceType;
  const category = searchParams[CATEGORY_PARAM];
  if (category) {
    spaceType = spaceTypeForCategoryParam(category, groups);
    if (!spaceType) {
      // Lounge or another category outside the three groups: keep it as a raw filter on /s.
      extraParams[CATEGORY_PARAM] = category;
      spaceType = null;
    }
  }
  PASSED_THROUGH.forEach(key => {
    const value = searchParams[key];
    if (value !== undefined && value !== null && value !== '') {
      extraParams[key] = value;
    }
  });
  return { where, spaceType, extraParams };
};

/**
 * Run the concierge for a sentence.
 *
 * @param {Object} p
 * @param {string} p.text the visitor's sentence
 * @param {Object} p.config marketplace config (categories, listing types)
 * @param {string} p.locale UI locale
 * @param {Array} p.groups resolved space-type groups
 * @returns {Promise<{ where: Object|null, spaceType: string|null|undefined, extraParams: Object,
 *   fallback: boolean }>} never rejects
 */
export const runConciergeSearch = async ({ text, config, locale, groups }) => {
  const trimmed = sliceWholeChars(`${text || ''}`.trim(), MAX_TEXT_LENGTH);
  const lang = `${locale || 'de'}`.split('-')[0];
  const fallback = () => ({ ...keywordSearch(trimmed), fallback: true });

  try {
    const { intent } = await aiConciergeParse(conciergeRequestPayload(trimmed, config, lang));
    let { searchParams, geocodeLocationName } = intentToSearchParams(
      intent,
      config?.listing?.listingTypes
    );

    if (geocodeLocationName) {
      const place = await geocodePlace(geocodeLocationName, lang);
      searchParams = place
        ? { ...searchParams, address: place.address, bounds: place.boundsStr }
        : mergeLocationIntoKeywords(searchParams, redactPii(geocodeLocationName));
    }
    if (searchParams.keywords) {
      searchParams = { ...searchParams, keywords: redactPii(searchParams.keywords) };
    }

    track.aiConciergeSearch({
      search_term: redactPii(trimmed),
      ai_fallback: 'no',
      ai_city:
        intent?.citySlug || (intent?.locationName && redactPii(intent.locationName)) || undefined,
      ai_category: intent?.categoryId || undefined,
    });

    if (Object.keys(searchParams).length === 0) {
      return fallback();
    }
    const split = splitConciergeParams(normalizeSearchParams(searchParams), {
      groups,
      citySlug: intent?.citySlug,
    });
    return { ...split, fallback: false };
  } catch (err) {
    if (err?.error === 'AI_CONCIERGE_DISABLED') {
      markAiConciergeUnavailable();
    }
    track.aiConciergeSearch({ search_term: redactPii(trimmed), ai_fallback: 'yes' });
    return fallback();
  }
};
